import React, { useState, useEffect } from 'react';
import { 
  getPrompts, 
  getTestSets,
  getAvailableModels 
} from '../../services/api';
import { MessageSquare, Database, Target, MessageCircle, Type, Settings, Info, Cpu, FileText, Hash, Plus, X, Eye, ToggleLeft, ToggleRight, BarChart3, Table, TrendingUp, GitBranch, Route, ArrowRight } from 'lucide-react';

const NodeDetailsPanel = ({ node, projectId, onNodeDataChange }) => {
  const [prompts, setPrompts] = useState([]);
  const [testSets, setTestSets] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [promptsData, testSetsData, modelsData] = await Promise.all([
        getPrompts(projectId),
        getTestSets(projectId),
        getAvailableModels()
      ]);
      setPrompts(promptsData);
      setTestSets(testSetsData);
      setModels(modelsData);
    } catch (error) {
      console.error('Error loading node details data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = (newData) => {
    onNodeDataChange(node.id, newData);
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner h-8 w-8 mx-auto mb-3"></div>
          <p className="text-sm text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  if (node.type === 'textFieldsNode') {
    // Helper function to extract variables from template
    const extractVariables = (template) => {
      const matches = template.match(/{([^}]+)}/g);
      return matches ? matches.map(match => match.slice(1, -1)) : [];
    };

    const addField = () => {
      const fields = [...(node.data.fields || [])];
      fields.push({ name: `variable${fields.length + 1}`, values: [''] });
      handleDataChange({ fields });
    };

    const removeField = (index) => {
      const fields = [...(node.data.fields || [])];
      fields.splice(index, 1);
      handleDataChange({ fields });
    };

    const updateFieldName = (index, name) => {
      const fields = [...(node.data.fields || [])];
      fields[index] = { ...fields[index], name };
      handleDataChange({ fields });
    };

    const addFieldValue = (fieldIndex) => {
      const fields = [...(node.data.fields || [])];
      fields[fieldIndex].values.push('');
      handleDataChange({ fields });
    };

    const removeFieldValue = (fieldIndex, valueIndex) => {
      const fields = [...(node.data.fields || [])];
      fields[fieldIndex].values.splice(valueIndex, 1);
      handleDataChange({ fields });
    };

    const updateFieldValue = (fieldIndex, valueIndex, value) => {
      const fields = [...(node.data.fields || [])];
      fields[fieldIndex].values[valueIndex] = value;
      handleDataChange({ fields });
    };

    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
              <Type className="h-5 w-5 text-gray-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">TextFields Node</h3>
              <p className="text-sm text-gray-600">Configure custom variables</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'TextFields Node'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Variable Fields */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Type className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Variable Fields</label>
                </div>
                <button
                  onClick={addField}
                  className="btn-secondary text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Field
                </button>
              </div>

              <div className="space-y-4">
                {(node.data.fields || []).map((field, fieldIndex) => (
                  <div key={fieldIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {/* Field Name */}
                    <div className="flex items-center space-x-2 mb-3">
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => updateFieldName(fieldIndex, e.target.value)}
                        className="flex-1 text-sm font-medium px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Variable name (e.g., 'game', 'color')"
                      />
                      {(node.data.fields || []).length > 1 && (
                        <button
                          onClick={() => removeField(fieldIndex)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Field Values */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Values:</label>
                      {field.values.map((value, valueIndex) => (
                        <div key={valueIndex} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => updateFieldValue(fieldIndex, valueIndex, e.target.value)}
                            className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-celadon-500 focus:border-celadon-500 bg-white"
                            placeholder="Enter value"
                          />
                          {field.values.length > 1 && (
                            <button
                              onClick={() => removeFieldValue(fieldIndex, valueIndex)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                      
                      {/* Add Value Button */}
                      <button
                        onClick={() => addFieldValue(fieldIndex)}
                        className="w-full py-2 text-xs text-gray-600 hover:text-gray-700 hover:bg-gray-50 border border-dashed border-gray-300 rounded transition-colors flex items-center justify-center space-x-1"
                      >
                        <Plus className="h-3 w-3" />
                        <span>Add value</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {(node.data.fields || []).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Type className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No fields defined yet</p>
                  <p className="text-xs text-gray-400">Click "Add Field" to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Usage Example */}
          {(node.data.fields || []).length > 0 && (
            <div className="card bg-gradient-to-br from-gray-50 to-white border-gray-200">
              <div className="card-body">
                <div className="flex items-center space-x-2 mb-3">
                  <Info className="h-4 w-4 text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Usage Example</h4>
                </div>
                <div className="text-xs text-gray-800 space-y-2">
                  <p className="font-medium">In a prompt template, use these variables:</p>
                  <div className="bg-gray-100 border border-gray-200 rounded p-2 font-mono text-gray-900">
                    {node.data.fields.map(field => `{${field.name}}`).join(', ')}
                  </div>
                  <p className="text-gray-700">
                    Example: "What was the first {`{${node.data.fields[0]?.name || 'variable'}}`} game?"
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  if (node.type === 'promptNode') {
    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-celadon-50 to-celadon-100 rounded-xl">
              <MessageSquare className="h-5 w-5 text-celadon-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Prompt Node</h3>
              <p className="text-sm text-gray-600">Configure LLM processing</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Model Selection */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Cpu className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">AI Model</label>
              </div>
              <select
                                  value={node.data.model || 'llama-3.1-8b-instant'}
                onChange={(e) => handleDataChange({ model: e.target.value })}
                className="input"
              >
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Prompt Type Toggle */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Prompt Type</label>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs ${node.data.promptType === 'existing' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    Existing
                  </span>
                  <button
                    onClick={() => {
                      const newType = node.data.promptType === 'custom' ? 'existing' : 'custom';
                      handleDataChange({ 
                        promptType: newType,
                        // Clear the other type's data
                        ...(newType === 'custom' ? {
                          promptId: null,
                          promptName: '',
                          versionId: null,
                          versionNumber: null
                        } : {
                          customTemplate: '',
                          templateVariables: []
                        })
                      });
                    }}
                    className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {node.data.promptType === 'custom' ? (
                      <ToggleRight className="h-5 w-5 text-celadon-600" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  <span className={`text-xs ${node.data.promptType === 'custom' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    Custom
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {node.data.promptType === 'custom' 
                  ? 'Create a custom prompt template with variables like {variable_name}'
                  : 'Select from your existing saved prompts'
                }
              </p>
            </div>
          </div>

          {/* Existing Prompt Configuration */}
          {node.data.promptType !== 'custom' && (
            <>
              {/* Prompt Selection */}
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center space-x-2 mb-3">
                    <FileText className="h-4 w-4 text-gray-600" />
                    <label className="text-sm font-semibold text-gray-900">Prompt</label>
                  </div>
                  <select
                    value={node.data.promptId || ''}
                    onChange={(e) => {
                      const selectedPrompt = prompts.find(p => p.id === e.target.value);
                      handleDataChange({ 
                        promptId: e.target.value,
                        promptName: selectedPrompt?.name || '',
                        versionId: selectedPrompt?.versions?.[0]?.id || null,
                        versionNumber: selectedPrompt?.versions?.[0]?.version_number || null
                      });
                    }}
                    className="input"
                  >
                    <option value="">Select a prompt...</option>
                    {prompts.map((prompt) => (
                      <option key={prompt.id} value={prompt.id}>
                        {prompt.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Version Selection */}
              {node.data.promptId && (
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center space-x-2 mb-3">
                      <Hash className="h-4 w-4 text-gray-600" />
                      <label className="text-sm font-semibold text-gray-900">Prompt Version</label>
                    </div>
                    <select
                      value={node.data.versionId || ''}
                      onChange={(e) => {
                        const selectedPrompt = prompts.find(p => p.id === node.data.promptId);
                        const selectedVersion = selectedPrompt?.versions?.find(v => v.id === e.target.value);
                        handleDataChange({ 
                          versionId: e.target.value,
                          versionNumber: selectedVersion?.version_number || null
                        });
                      }}
                      className="input"
                    >
                      <option value="">Select a version...</option>
                      {prompts
                        .find(p => p.id === node.data.promptId)
                        ?.versions?.map((version) => (
                          <option key={version.id} value={version.id}>
                            v{version.version_number} {version.is_active && '(Active)'}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Custom Template Configuration */}
          {node.data.promptType === 'custom' && (
            <div className="card">
              <div className="card-body">
                <div className="flex items-center space-x-2 mb-3">
                  <Type className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Custom Template</label>
                </div>
                <textarea
                  value={node.data.customTemplate || ''}
                  onChange={(e) => {
                    const template = e.target.value;
                    const variables = template.match(/{([^}]+)}/g)?.map(match => match.slice(1, -1)) || [];
                    handleDataChange({ 
                      customTemplate: template,
                      templateVariables: variables
                    });
                  }}
                  className="input min-h-[100px] resize-y"
                  placeholder="Enter your prompt template here. Use {variable_name} for variables.&#10;&#10;Example: What was the first {game} game released in {year}?"
                />
                
                {/* Show detected variables */}
                {node.data.templateVariables && node.data.templateVariables.length > 0 && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-gray-700 mb-2 block">
                      Detected Variables:
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {node.data.templateVariables.map((variable, index) => (
                        <span 
                          key={index}
                          className="text-xs bg-celadon-100 text-celadon-700 px-2 py-1 rounded-full"
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  Variables should be connected from TextFields nodes or other data sources.
                </p>
              </div>
            </div>
          )}

          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'Prompt Node'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Node Information */}
          <div className="card bg-gradient-to-br from-celadon-50 to-white border-celadon-200">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Info className="h-4 w-4 text-celadon-600" />
                <h4 className="text-sm font-semibold text-celadon-900">Node Information</h4>
              </div>
              <div className="space-y-2 text-xs text-celadon-800">
                <div className="flex justify-between">
                  <span className="font-medium">ID:</span>
                  <span className="font-mono text-celadon-700">{node.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Type:</span>
                  <span>Prompt Processing Node</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Inputs:</span>
                  <span>Text from connected nodes</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Outputs:</span>
                  <span>LLM response text</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (node.type === 'testSetNode') {
    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
              <Database className="h-5 w-5 text-gray-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Test Set Node</h3>
              <p className="text-sm text-gray-600">Configure input data</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Test Set Selection */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Database className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Test Set</label>
              </div>
              <select
                value={node.data.testSetId || ''}
                onChange={(e) => {
                  const selectedTestSet = testSets.find(ts => ts.id === e.target.value);
                  handleDataChange({ 
                    testSetId: e.target.value,
                    testSetName: selectedTestSet?.name || '',
                    testCaseCount: selectedTestSet?.test_case_count || 0
                  });
                }}
                className="input"
              >
                <option value="">Select a test set...</option>
                {testSets.map((testSet) => (
                  <option key={testSet.id} value={testSet.id}>
                    {testSet.name} ({testSet.test_case_count || 0} cases)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'Test Set Node'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Test Set Preview */}
          {node.data.testSetId && (
            <div className="card bg-gradient-to-br from-gray-50 to-white border-gray-200">
              <div className="card-body">
                <div className="flex items-center space-x-2 mb-3">
                  <Database className="h-4 w-4 text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Selected Test Set</h4>
                </div>
                <div className="space-y-2 text-xs text-gray-700">
                  <div className="flex justify-between">
                    <span className="font-medium">Name:</span>
                    <span>{node.data.testSetName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Test Cases:</span>
                    <span className="badge badge-secondary">{node.data.testCaseCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Output:</span>
                    <span>Each test case input</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Node Information */}
          <div className="card bg-gradient-to-br from-gray-50 to-white border-gray-200">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Info className="h-4 w-4 text-gray-600" />
                <h4 className="text-sm font-semibold text-gray-900">Node Information</h4>
              </div>
              <div className="space-y-2 text-xs text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">ID:</span>
                  <span className="font-mono text-gray-600">{node.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Type:</span>
                  <span>Test Data Input Node</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Inputs:</span>
                  <span>None (data source)</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Outputs:</span>
                  <span>Test case input text</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (node.type === 'autoEvaluatorNode') {
    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
              <Target className="h-5 w-5 text-gray-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Auto-Evaluator</h3>
              <p className="text-sm text-gray-600">Configure evaluation criteria</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Evaluation Model Selection */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Cpu className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Evaluation Model</label>
              </div>
              <select
                                  value={node.data.evaluation_model || 'llama-3.1-8b-instant'}
                onChange={(e) => handleDataChange({ evaluation_model: e.target.value })}
                className="input"
              >
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.description}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Model used for running evaluations
              </p>
            </div>
          </div>

          {/* Pass Threshold */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">
                  Pass Threshold: {Math.round((node.data.pass_threshold || 0.7) * 100)}%
                </label>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={node.data.pass_threshold || 0.7}
                onChange={(e) => handleDataChange({ pass_threshold: parseFloat(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Minimum score required for a response to pass
              </p>
            </div>
          </div>

          {/* Evaluation Criteria */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Evaluation Criteria</label>
                </div>
                <button
                  onClick={() => {
                    const newCriteria = [...(node.data.criteria || []), {
                      name: 'New Criterion',
                      description: 'Describe what to evaluate...',
                      weight: 20
                    }];
                    handleDataChange({ criteria: newCriteria });
                  }}
                                        className="btn btn-ghost !text-gray-600 !p-1"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                {(node.data.criteria || []).map((criterion, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <input
                        type="text"
                        value={criterion.name}
                        onChange={(e) => {
                          const newCriteria = [...(node.data.criteria || [])];
                          newCriteria[index] = { ...criterion, name: e.target.value };
                          handleDataChange({ criteria: newCriteria });
                        }}
                        className="text-sm font-medium bg-transparent border-none outline-none flex-1"
                        placeholder="Criterion name..."
                      />
                      <button
                        onClick={() => {
                          const newCriteria = (node.data.criteria || []).filter((_, i) => i !== index);
                          handleDataChange({ criteria: newCriteria });
                        }}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    
                    <textarea
                      value={criterion.description}
                      onChange={(e) => {
                        const newCriteria = [...(node.data.criteria || [])];
                        newCriteria[index] = { ...criterion, description: e.target.value };
                        handleDataChange({ criteria: newCriteria });
                      }}
                      className="w-full text-xs bg-transparent border border-gray-300 rounded px-2 py-1 resize-none"
                      rows="2"
                      placeholder="Describe what to evaluate for this criterion..."
                    />
                    
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-600">Weight (%):</span>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={criterion.weight}
                        onChange={(e) => {
                          const newCriteria = [...(node.data.criteria || [])];
                          newCriteria[index] = { ...criterion, weight: parseInt(e.target.value) || 10 };
                          handleDataChange({ criteria: newCriteria });
                        }}
                        className="w-16 text-xs text-right bg-white border border-gray-300 rounded px-1 py-0.5"
                      />
                    </div>
                  </div>
                ))}
                
                {(!node.data.criteria || node.data.criteria.length === 0) && (
                  <div className="text-center py-6 text-gray-500">
                    <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No evaluation criteria defined</p>
                    <p className="text-xs">Click the + button to add criteria</p>
                  </div>
                )}
              </div>
              
              {/* Weight Validation */}
              {(node.data.criteria || []).length > 0 && (
                <div className="mt-3 p-3 rounded-lg border">
                  {(() => {
                    const totalWeight = (node.data.criteria || []).reduce((sum, c) => sum + (c.weight || 0), 0);
                    const isValid = totalWeight === 100;
                    
                    return (
                      <div className={`text-xs ${isValid ? 'text-celadon-700 bg-celadon-50' : 'text-amber-700 bg-amber-50'} border ${isValid ? 'border-celadon-200' : 'border-amber-200'} rounded p-2`}>
                        <div className="flex items-center space-x-2">
                          {isValid ? (
                            <div className="w-2 h-2 bg-celadon-500 rounded-full"></div>
                          ) : (
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                          )}
                          <span className="font-medium">
                            Total Weight: {totalWeight}%
                          </span>
                        </div>
                        {!isValid && (
                          <div className="mt-1 text-xs">
                            Weights should add up to 100% for accurate evaluation.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'Auto-Evaluator'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Node Information */}
                      <div className="card bg-gradient-to-br from-gray-50 to-white border-gray-200">
              <div className="card-body">
                <div className="flex items-center space-x-2 mb-3">
                  <Info className="h-4 w-4 text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Node Information</h4>
                </div>
                <div className="space-y-2 text-xs text-gray-800">
                  <div className="flex justify-between">
                    <span className="font-medium">ID:</span>
                    <span className="font-mono text-gray-700">{node.id}</span>
                  </div>
                <div className="flex justify-between">
                  <span className="font-medium">Type:</span>
                  <span>Auto-Evaluation Node</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Inputs:</span>
                  <span>LLM responses from prompt nodes</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Outputs:</span>
                  <span>Evaluation scores and reasoning</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Criteria:</span>
                  <span>{(node.data.criteria || []).length} defined</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }



  if (node.type === 'responseNode') {
    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
                <MessageCircle className="h-5 w-5 text-gray-700" />
              </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Response Node</h3>
              <p className="text-sm text-gray-600">Configure response display</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Display Mode */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Eye className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Display Mode</label>
              </div>
              <select
                value={node.data.displayMode || 'preview'}
                onChange={(e) => handleDataChange({ displayMode: e.target.value })}
                className="input"
              >
                <option value="preview">Preview - Show truncated text</option>
                <option value="full">Full Text - Show complete response</option>
                <option value="summary">Summary - Show key information only</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Choose how responses should be displayed in the node
              </p>
            </div>
          </div>

          {/* Max Length (for preview mode) */}
          {node.data.displayMode === 'preview' && (
            <div className="card">
              <div className="card-body">
                <div className="flex items-center space-x-2 mb-3">
                  <Hash className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Preview Length</label>
                </div>
                <input
                  type="number"
                  min="50"
                  max="500"
                  step="10"
                  value={node.data.maxLength || 200}
                  onChange={(e) => handleDataChange({ maxLength: parseInt(e.target.value) || 200 })}
                  className="input"
                  placeholder="Maximum characters to show..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  Maximum number of characters to display in preview mode
                </p>
              </div>
            </div>
          )}

          {/* Show Metadata */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Info className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Show Metadata</label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={node.data.showMetadata !== false}
                    onChange={(e) => handleDataChange({ showMetadata: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-celadon-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Display additional information like model name, token count, etc.
              </p>
            </div>
          </div>

          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'Response Display'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Node Information */}
          <div className="card bg-gradient-to-br from-gray-50 to-white border-gray-200">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Info className="h-4 w-4 text-gray-600" />
                <h4 className="text-sm font-semibold text-gray-900">Node Information</h4>
              </div>
              <div className="space-y-2 text-xs text-gray-800">
                <div className="flex justify-between">
                  <span className="font-medium">ID:</span>
                  <span className="font-mono text-gray-700">{node.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Type:</span>
                  <span>Response Display Node</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Inputs:</span>
                  <span>LLM responses from any source</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Outputs:</span>
                  <span>None (terminal node)</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Purpose:</span>
                  <span>Display and preview responses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (node.type === 'visualizerNode') {
    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <BarChart3 className="h-5 w-5 text-blue-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Visualizer Node</h3>
              <p className="text-sm text-gray-600">Configure data visualization</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Visualization Type */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <TrendingUp className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Default Visualization Type</label>
              </div>
              <select
                value={node.data.visualizationType || 'table'}
                onChange={(e) => handleDataChange({ visualizationType: e.target.value })}
                className="input"
              >
                <option value="table">Table - Structured data view</option>
                <option value="summary">Summary - Key statistics and info</option>
                <option value="chart">Chart - Visual data representation</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Default view when the node receives data
              </p>
            </div>
          </div>

          {/* Max Display Items */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Hash className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Max Display Items</label>
              </div>
              <input
                type="number"
                min="5"
                max="100"
                step="5"
                value={node.data.maxDisplayItems || 10}
                onChange={(e) => handleDataChange({ maxDisplayItems: parseInt(e.target.value) || 10 })}
                className="input"
                placeholder="Maximum items to display..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Maximum number of items to show in table view for performance
              </p>
            </div>
          </div>

          {/* Show Data Types */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Type className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Show Data Types</label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={node.data.showTypes !== false}
                    onChange={(e) => handleDataChange({ showTypes: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Display data type information in visualizations
              </p>
            </div>
          </div>

          {/* Auto Refresh */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Eye className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Auto Refresh</label>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={node.data.autoRefresh !== false}
                    onChange={(e) => handleDataChange({ autoRefresh: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Automatically update visualization when connected data changes
              </p>
            </div>
          </div>

          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'Visualizer Node'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="card bg-gradient-to-br from-blue-50 to-blue-25 border-blue-200">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Info className="h-4 w-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-blue-900">How to Use</h4>
              </div>
              <div className="space-y-2 text-xs text-blue-800">
                <div className="flex items-start space-x-2">
                  <span className="font-medium">1.</span>
                  <span>Connect this node to any data source (prompt node, test set, etc.)</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">2.</span>
                  <span>Run the flow to populate the visualizer with data</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">3.</span>
                  <span>Switch between table, summary, and chart views using the controls</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">4.</span>
                  <span>Use the visualizer to analyze patterns and data distribution</span>
                </div>
              </div>
            </div>
          </div>

          {/* Node Information */}
          <div className="card bg-gradient-to-br from-gray-50 to-white border-gray-200">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Info className="h-4 w-4 text-gray-600" />
                <h4 className="text-sm font-semibold text-gray-900">Node Information</h4>
              </div>
              <div className="space-y-2 text-xs text-gray-800">
                <div className="flex justify-between">
                  <span className="font-medium">ID:</span>
                  <span className="font-mono text-gray-700">{node.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Type:</span>
                  <span>Data Visualization Node</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Inputs:</span>
                  <span>Any data from connected nodes</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Outputs:</span>
                  <span>Processed visualization data</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Purpose:</span>
                  <span>Analyze and visualize data flows</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (node.type === 'splitNode') {
    const addBranch = () => {
      const branches = [...(node.data.branches || [])];
      const branchIndex = branches.length + 1;
      branches.push({ 
        id: `branch-${branchIndex}`, 
        name: `Branch ${String.fromCharCode(64 + branchIndex)}`, 
        condition: {
          type: 'expression',
          field: 'response',
          operator: 'contains',
          value: ''
        }
      });
      handleDataChange({ branches });
    };

    const removeBranch = (index) => {
      const branches = [...(node.data.branches || [])];
      branches.splice(index, 1);
      handleDataChange({ branches });
    };

    const updateBranch = (index, branchData) => {
      const branches = [...(node.data.branches || [])];
      branches[index] = { ...branches[index], ...branchData };
      handleDataChange({ branches });
    };

    const updateBranchCondition = (branchIndex, conditionData) => {
      const branches = [...(node.data.branches || [])];
      branches[branchIndex].condition = { ...branches[branchIndex].condition, ...conditionData };
      handleDataChange({ branches });
    };

    return (
      <div className="h-full bg-gray-50">
        {/* Header */}
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
              <GitBranch className="h-5 w-5 text-purple-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Split Node</h3>
              <p className="text-sm text-gray-600">Configure conditional branching</p>
            </div>
          </div>
        </div>
        
        {/* Configuration Form */}
        <div className="p-6 space-y-6">
          {/* Node Label */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Node Label</label>
              </div>
              <input
                type="text"
                value={node.data.label || 'Split Node'}
                onChange={(e) => handleDataChange({ label: e.target.value })}
                className="input"
                placeholder="Enter node label..."
              />
            </div>
          </div>

          {/* Split Type */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Route className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Split Type</label>
              </div>
              <select
                value={node.data.splitType || 'condition'}
                onChange={(e) => handleDataChange({ splitType: e.target.value })}
                className="input"
              >
                <option value="condition">Conditional Split</option>
                <option value="parallel">Parallel Split (All Branches)</option>
                <option value="percentage">Percentage Split</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                {node.data.splitType === 'parallel' 
                  ? 'Data will be sent to all branches simultaneously'
                  : node.data.splitType === 'percentage'
                  ? 'Data will be distributed by percentage across branches'
                  : 'Data will be routed based on conditional logic'}
              </p>
            </div>
          </div>

          {/* Branches Configuration */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <GitBranch className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Branches</label>
                </div>
                <button
                  onClick={addBranch}
                  className="btn-secondary text-xs"
                  disabled={(node.data.branches || []).length >= 5}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Branch
                </button>
              </div>

              <div className="space-y-4">
                {(node.data.branches || [{ id: 'branch-1', name: 'Branch A', condition: null }, { id: 'branch-2', name: 'Branch B', condition: null }]).map((branch, branchIndex) => (
                  <div key={branch.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {/* Branch Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-4 h-4 rounded-full border-2"
                          style={{ borderColor: ['#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4'][branchIndex] || '#6B7280' }}
                        />
                        <input
                          type="text"
                          value={branch.name}
                          onChange={(e) => updateBranch(branchIndex, { name: e.target.value })}
                          className="text-sm font-medium px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          placeholder="Branch name"
                        />
                      </div>
                      {(node.data.branches || []).length > 2 && (
                        <button
                          onClick={() => removeBranch(branchIndex)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Branch Condition (only for conditional splits) */}
                    {node.data.splitType !== 'parallel' && (
                      <div className="space-y-3">
                        <label className="text-xs font-medium text-gray-700">Condition:</label>
                        
                        {/* Condition Type */}
                        <select
                          value={branch.condition?.type || 'expression'}
                          onChange={(e) => updateBranchCondition(branchIndex, { type: e.target.value })}
                          className="text-sm px-3 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white w-full"
                        >
                          <option value="expression">Expression</option>
                          <option value="score">Score Based</option>
                          <option value="contains">Contains Text</option>
                          <option value="regex">Regular Expression</option>
                        </select>
                        
                        {/* Condition Fields */}
                        <div className="grid grid-cols-3 gap-2">
                          {/* Field */}
                          <select
                            value={branch.condition?.field || 'response'}
                            onChange={(e) => updateBranchCondition(branchIndex, { field: e.target.value })}
                            className="text-sm px-2 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white"
                          >
                            <option value="response">Response</option>
                            <option value="input">Input</option>
                            <option value="score">Score</option>
                            <option value="tokens">Token Count</option>
                          </select>
                          
                          {/* Operator */}
                          <select
                            value={branch.condition?.operator || 'contains'}
                            onChange={(e) => updateBranchCondition(branchIndex, { operator: e.target.value })}
                            className="text-sm px-2 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white"
                          >
                            <option value="contains">contains</option>
                            <option value="equals">equals</option>
                            <option value="startsWith">starts with</option>
                            <option value="endsWith">ends with</option>
                            <option value=">">&gt;</option>
                            <option value="<">&lt;</option>
                            <option value=">=">&gt;=</option>
                            <option value="<=">&lt;=</option>
                            <option value="regex">matches regex</option>
                          </select>
                          
                          {/* Value */}
                          <input
                            type="text"
                            value={branch.condition?.value || ''}
                            onChange={(e) => updateBranchCondition(branchIndex, { value: e.target.value })}
                            className="text-sm px-2 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white"
                            placeholder="Value"
                          />
                        </div>
                        
                        {/* Condition Preview */}
                        {branch.condition?.field && branch.condition?.operator && branch.condition?.value && (
                          <div className="text-xs bg-purple-50 border border-purple-200 rounded p-2 text-purple-800">
                            <span className="font-medium">Condition:</span> {branch.condition.field} {branch.condition.operator} "{branch.condition.value}"
                          </div>
                        )}
                      </div>
                    )}

                    {/* Percentage Split Configuration */}
                    {node.data.splitType === 'percentage' && (
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700">Percentage:</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={branch.percentage || 0}
                            onChange={(e) => updateBranch(branchIndex, { percentage: parseInt(e.target.value) || 0 })}
                            className="text-sm px-3 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white w-20"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Default Branch */}
          {node.data.splitType === 'condition' && (
            <div className="card">
              <div className="card-body">
                <div className="flex items-center space-x-2 mb-3">
                  <ArrowRight className="h-4 w-4 text-gray-600" />
                  <label className="text-sm font-semibold text-gray-900">Default Branch</label>
                </div>
                <select
                  value={node.data.defaultBranch || ''}
                  onChange={(e) => handleDataChange({ defaultBranch: e.target.value })}
                  className="input"
                >
                  <option value="">No default branch</option>
                  {(node.data.branches || []).map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Branch to use when no conditions are met
                </p>
              </div>
            </div>
          )}

          {/* Aggregation Mode */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <TrendingUp className="h-4 w-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-900">Result Aggregation</label>
              </div>
              <select
                value={node.data.aggregationMode || 'all'}
                onChange={(e) => handleDataChange({ aggregationMode: e.target.value })}
                className="input"
              >
                <option value="all">Wait for All Branches</option>
                <option value="first">Use First Completed</option>
                <option value="majority">Use Majority Result</option>
                <option value="custom">Custom Logic</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                How to handle results from multiple active branches
              </p>
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="card bg-gradient-to-br from-purple-50 to-purple-25 border-purple-200">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-3">
                <Info className="h-4 w-4 text-purple-600" />
                <h4 className="text-sm font-semibold text-purple-900">How to Use</h4>
              </div>
              <div className="space-y-2 text-xs text-purple-800">
                <div className="flex items-start space-x-2">
                  <span className="font-medium">1.</span>
                  <span>Configure branches with names and conditions</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">2.</span>
                  <span>Connect input data to the split node</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">3.</span>
                  <span>Connect each branch output to different downstream nodes</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">4.</span>
                  <span>Data will be routed based on your conditions during execution</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="card-header">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
            <Settings className="h-5 w-5 text-gray-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Node Settings</h3>
            <p className="text-sm text-gray-600">Select a node to configure</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Settings className="h-8 w-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Node Selected</h4>
            <p className="text-sm text-gray-600">
              Click on a node in the canvas to view and edit its configuration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NodeDetailsPanel; 